import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  CheckCircle2,
  XCircle,
  Target,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Loader2,
  BookOpen,
  Edit3,
  Save,
  X as XIcon,
  Trash2,
  TrendingUp,
  History,
  ArrowRight,
  Undo2,
  Goal,
  Plus,
  TrendingDown,
  Info
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { motion, AnimatePresence } from 'framer-motion'
import ObjectiveCard from './ObjectiveCard'
import type { PendingObjective, RejectedObjective } from './types'
import { getFunctionalAreaColor, getPillarColor } from './types'
// Using native title attr for KPI descriptions — Radix tooltips stick on fast hover
import { StrategicContextSelector, type StrategicContext } from '@/components/strategic/StrategicContextSelector'
import type { KPI } from './types'
import { yearEndApprovalNote } from './yearEndMeeting'
import toast from 'react-hot-toast'

interface ObjectiveListTabProps {
  tabType: 'pending' | 'active' | 'rejected' | 'all'
  objectives: PendingObjective[]
  displayMemberName: string
  expandedObjective: number | null
  setExpandedObjective: (id: number | null) => void
  editingObjective: number | null
  editedObjectives: Record<number, PendingObjective>
  savingEdit: number | null
  processingObjective: number | null
  deletingObjective: number | null
  withdrawingObjective: number | null
  canApproveObjectives: boolean
  canWithdrawObjectives: boolean
  userRole: 'manager' | 'employee'
  approvalFeedback: Record<number, string>
  setApprovalFeedback: (fn: (prev: Record<number, string>) => Record<number, string>) => void
  objectiveChanges: Record<number, any[]>
  loadingChanges: Record<number, boolean>
  collaborativeSession: any
  /** The year these objectives are for (the note when they are agreed in the year before's year-end check-in). */
  year?: number
  objectivesLookup: any[]
  initiativesLookup: any[]
  operationalLookup: any[]
  getCurrentObjective: (objective: PendingObjective) => PendingObjective
  onStartEdit: (objective: PendingObjective) => void
  onCancelEdit: (objectiveId: number) => void
  onSaveEdit: (objectiveId: number) => void
  onUpdateField: (objectiveId: number, field: keyof PendingObjective, value: any) => void
  onUpdateKPIField: (objectiveId: number, kpiId: string, field: string, value: any) => void
  onAddKPI: (objectiveId: number) => void
  onRemoveKPI: (objectiveId: number, kpiId: string) => void
  onSetEditedObjectives: (fn: (prev: Record<number, PendingObjective>) => Record<number, PendingObjective>) => void
  onApprove: (objectiveId: number) => void
  onShowRejectDialog: (id: number) => void
  onShowDeleteDialog: (id: number) => void
  onShowWithdrawDialog: (id: number) => void
}

export default function ObjectiveListTab({
  tabType,
  objectives,
  displayMemberName,
  expandedObjective,
  setExpandedObjective,
  editingObjective,
  editedObjectives,
  savingEdit,
  processingObjective,
  deletingObjective,
  withdrawingObjective,
  canApproveObjectives,
  canWithdrawObjectives,
  userRole,
  approvalFeedback,
  setApprovalFeedback,
  objectiveChanges,
  loadingChanges,
  collaborativeSession,
  year,
  objectivesLookup,
  initiativesLookup,
  operationalLookup,
  getCurrentObjective,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onUpdateField,
  onUpdateKPIField,
  onAddKPI,
  onRemoveKPI,
  onSetEditedObjectives,
  onApprove,
  onShowRejectDialog,
  onShowDeleteDialog,
  onShowWithdrawDialog
}: ObjectiveListTabProps) {
  const [expandedKpiId, setExpandedKpiId] = useState<string | null>(null)

  const formatKpiMeasurement = (kpi: KPI) => {
    const mode = kpi.measurement_mode || 'numeric'
    if (mode === 'rating') return 'RAG'
    if (mode === 'binary') return 'Yes/No'
    return `${kpi.target_value} ${kpi.unit}`
  }

  const kpiDirectionIcon = (kpi: KPI) => {
    if (kpi.measurement_mode === 'rating' || kpi.measurement_mode === 'binary') return null
    return kpi.measurement_direction === 'lower_is_better'
      ? <TrendingDown className="h-3 w-3 text-blue-500 inline ml-1" />
      : <TrendingUp className="h-3 w-3 text-green-500 inline ml-1" />
  }

  // Empty states
  if (objectives.length === 0) {
    if (tabType === 'all') {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <Goal className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No Objectives Found</h3>
            <p className="text-muted-foreground">
              {displayMemberName} doesn't have any objectives yet.
            </p>
          </CardContent>
        </Card>
      )
    }
    if (tabType === 'pending') {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
            <h3 className="text-lg font-semibold mb-2">All Clear!</h3>
            <p className="text-muted-foreground">
              No objectives pending your approval for {displayMemberName}.
            </p>
          </CardContent>
        </Card>
      )
    }
    if (tabType === 'active') {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <Goal className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold mb-2">No Active Objectives</h3>
            <p className="text-muted-foreground">
              {displayMemberName} doesn't have any active objectives yet.
            </p>
          </CardContent>
        </Card>
      )
    }
    if (tabType === 'rejected') {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
            <h3 className="text-lg font-semibold mb-2">No Rejection History</h3>
            <p className="text-muted-foreground">
              You haven't rejected any objectives for {displayMemberName}.
            </p>
          </CardContent>
        </Card>
      )
    }
  }

  const getSubheading = () => {
    switch (tabType) {
      case 'pending': return 'Review and approve team member-initiated objectives'
      case 'active': return `View and edit active objectives for ${displayMemberName}`
      case 'rejected': return "Review objectives you've rejected with feedback"
      default: return null
    }
  }

  const getBorderColor = (status: string) => {
    if (status === 'pending_approval') return 'border-l-amber-500'
    if (status === 'active') return 'border-l-emerald-500'
    return 'border-l-red-500'
  }

  const getStatusIcon = (status: string) => {
    if (status === 'pending_approval') return AlertCircle
    if (status === 'active') return CheckCircle2
    return XCircle
  }

  const getIconClass = (status: string) => {
    if (status === 'pending_approval') return 'text-amber-600'
    if (status === 'active') return 'text-emerald-600'
    return 'text-red-600'
  }

  const subheading = getSubheading()

  // For "all" tab, use the unified rendering from the original
  if (tabType === 'all') {
    return (
      <AnimatePresence mode="popLayout">
        {objectives.map((objective) => {
          const borderClass = getBorderColor(objective.status)
          const iconClass = getIconClass(objective.status)
          const Icon = getStatusIcon(objective.status)

          return (
            <motion.div
              key={objective.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -100 }}
            >
              <Card className={`overflow-hidden border-l-4 ${borderClass}`}>
                <CardHeader
                  className="pb-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedObjective(
                    expandedObjective === objective.id ? null : objective.id
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${iconClass}`} />
                        <CardTitle className="text-lg">{objective.title}</CardTitle>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Badge className={
                          objective.status === 'pending_approval' ? 'bg-amber-100 text-amber-800' :
                          objective.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {objective.status === 'pending_approval' ? 'Pending' :
                           objective.status === 'active' ? 'Active' : 'Rejected'}
                        </Badge>
                        {objective.kpa && (
                          <Badge className={getFunctionalAreaColor(objective.kpa.name)}>
                            <BookOpen className="h-3 w-3 mr-1" />
                            {objective.kpa.display_name}
                          </Badge>
                        )}
                        {objective.pillar && !objective.strategic_objective_id && (
                          <Badge className={getPillarColor(objective.pillar.name)}>
                            {objective.pillar.name}
                          </Badge>
                        )}
                        {objective.strategic_objective_id && objectivesLookup.find(o => o.id === objective.strategic_objective_id) && (() => {
                          const strategicObj = objectivesLookup.find(o => o.id === objective.strategic_objective_id)!
                          return (
                            <span title={`L1 Strategic Objective: ${strategicObj.display_name}`}>
                              <Badge
                                style={{ backgroundColor: strategicObj.color_code || '#6B7280' }}
                                className="text-white"
                              >
                                {strategicObj.code}. {strategicObj.display_name}
                              </Badge>
                            </span>
                          )
                        })()}
                        {objective.strategic_initiative_id && initiativesLookup.find(i => i.id === objective.strategic_initiative_id) && (() => {
                          const initiative = initiativesLookup.find(i => i.id === objective.strategic_initiative_id)!
                          return (
                            <span title={`L2 Strategic Initiative: ${initiative.name}`}>
                              <Badge variant="outline" className="text-xs">
                                {initiative.code}
                              </Badge>
                            </span>
                          )
                        })()}
                        {objective.status === 'active' && (
                          <Badge className="bg-emerald-100 text-emerald-800">
                            <TrendingUp className="h-3 w-3 mr-1" />
                            {objective.progress || 0}% Complete
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      {expandedObjective === objective.id ? (
                        <ChevronUp className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                  </div>
                </CardHeader>

                <AnimatePresence>
                  {expandedObjective === objective.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ObjectiveCard
                        objective={objective}
                        allowEdit={objective.status !== 'rejected'}
                        isEditing={editingObjective === objective.id}
                        currentObj={getCurrentObjective(objective)}
                        savingEdit={savingEdit}
                        editedObjectives={editedObjectives}
                        canWithdrawObjectives={canWithdrawObjectives}
                        withdrawingObjective={withdrawingObjective}
                        userRole={userRole}
                        objectivesLookup={objectivesLookup}
                        initiativesLookup={initiativesLookup}
                        operationalLookup={operationalLookup}
                        onStartEdit={onStartEdit}
                        onCancelEdit={onCancelEdit}
                        onSaveEdit={onSaveEdit}
                        onUpdateField={onUpdateField}
                        onUpdateKPIField={onUpdateKPIField}
                        onAddKPI={onAddKPI}
                        onRemoveKPI={onRemoveKPI}
                        onSetEditedObjectives={onSetEditedObjectives}
                        onShowWithdrawDialog={onShowWithdrawDialog}
                        onShowDeleteDialog={onShowDeleteDialog}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>
          )
        })}
      </AnimatePresence>
    )
  }

  // Active tab
  if (tabType === 'active') {
    return (
      <>
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-muted-foreground">{subheading}</p>
        </div>
        <AnimatePresence mode="popLayout">
          {objectives.map((objective) => (
            <motion.div
              key={objective.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -100 }}
            >
              <Card className="overflow-hidden border-l-4 border-l-emerald-500">
                <CardHeader
                  className="pb-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedObjective(
                    expandedObjective === objective.id ? null : objective.id
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        <CardTitle className="text-lg">{objective.title}</CardTitle>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {objective.kpa && (
                          <Badge className={getFunctionalAreaColor(objective.kpa.name)}>
                            <BookOpen className="h-3 w-3 mr-1" />
                            {objective.kpa.display_name}
                          </Badge>
                        )}
                        {objective.pillar && !objective.strategic_objective_id && (
                          <Badge className={getPillarColor(objective.pillar.name)}>
                            {objective.pillar.name}
                          </Badge>
                        )}
                        {objective.strategic_objective_id && objectivesLookup.find(o => o.id === objective.strategic_objective_id) && (() => {
                          const strategicObj = objectivesLookup.find(o => o.id === objective.strategic_objective_id)!
                          return (
                            <span title={`L1 Strategic Objective: ${strategicObj.display_name}`}>
                              <Badge
                                style={{ backgroundColor: strategicObj.color_code || '#6B7280' }}
                                className="text-white"
                              >
                                {strategicObj.code}. {strategicObj.display_name}
                              </Badge>
                            </span>
                          )
                        })()}
                        {objective.strategic_initiative_id && initiativesLookup.find(i => i.id === objective.strategic_initiative_id) && (() => {
                          const initiative = initiativesLookup.find(i => i.id === objective.strategic_initiative_id)!
                          return (
                            <span title={`L2 Strategic Initiative: ${initiative.name}`}>
                              <Badge variant="outline" className="text-xs">
                                {initiative.code}
                              </Badge>
                            </span>
                          )
                        })()}
                        <Badge className="bg-emerald-100 text-emerald-800">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          {objective.progress || 0}% Complete
                        </Badge>
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      {expandedObjective === objective.id ? (
                        <ChevronUp className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                  </div>
                </CardHeader>

                <AnimatePresence>
                  {expandedObjective === objective.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ObjectiveCard
                        objective={objective}
                        isEditing={editingObjective === objective.id}
                        currentObj={getCurrentObjective(objective)}
                        savingEdit={savingEdit}
                        editedObjectives={editedObjectives}
                        canWithdrawObjectives={canWithdrawObjectives}
                        withdrawingObjective={withdrawingObjective}
                        userRole={userRole}
                        objectivesLookup={objectivesLookup}
                        initiativesLookup={initiativesLookup}
                        operationalLookup={operationalLookup}
                        onStartEdit={onStartEdit}
                        onCancelEdit={onCancelEdit}
                        onSaveEdit={onSaveEdit}
                        onUpdateField={onUpdateField}
                        onUpdateKPIField={onUpdateKPIField}
                        onAddKPI={onAddKPI}
                        onRemoveKPI={onRemoveKPI}
                        onSetEditedObjectives={onSetEditedObjectives}
                        onShowWithdrawDialog={onShowWithdrawDialog}
                        onShowDeleteDialog={onShowDeleteDialog}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </>
    )
  }

  // Rejected tab
  if (tabType === 'rejected') {
    return (
      <>
        <div className="flex items-center justify-between mb-4">
          <p className="text-muted-foreground">{subheading}</p>
        </div>
        <AnimatePresence mode="popLayout">
          {objectives.map((objective) => (
            <motion.div
              key={objective.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -100 }}
            >
              <Card className="overflow-hidden border-l-4 border-l-red-500">
                <CardHeader
                  className="pb-3 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedObjective(
                    expandedObjective === objective.id ? null : objective.id
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-red-600" />
                        <CardTitle className="text-lg">{objective.title}</CardTitle>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {objective.kpa && (
                          <Badge className={getFunctionalAreaColor(objective.kpa.name)}>
                            <BookOpen className="h-3 w-3 mr-1" />
                            {objective.kpa.display_name}
                          </Badge>
                        )}
                        {objective.pillar && !objective.strategic_objective_id && (
                          <Badge className={getPillarColor(objective.pillar.name)}>
                            {objective.pillar.name}
                          </Badge>
                        )}
                        {objective.strategic_objective_id && objectivesLookup.find(o => o.id === objective.strategic_objective_id) && (() => {
                          const strategicObj = objectivesLookup.find(o => o.id === objective.strategic_objective_id)!
                          return (
                            <span title={`L1 Strategic Objective: ${strategicObj.display_name}`}>
                              <Badge
                                style={{ backgroundColor: strategicObj.color_code || '#6B7280' }}
                                className="text-white"
                              >
                                {strategicObj.code}. {strategicObj.display_name}
                              </Badge>
                            </span>
                          )
                        })()}
                        {objective.strategic_initiative_id && initiativesLookup.find(i => i.id === objective.strategic_initiative_id) && (() => {
                          const initiative = initiativesLookup.find(i => i.id === objective.strategic_initiative_id)!
                          return (
                            <span title={`L2 Strategic Initiative: ${initiative.name}`}>
                              <Badge variant="outline" className="text-xs">
                                {initiative.code}
                              </Badge>
                            </span>
                          )
                        })()}
                        <Badge variant="destructive">
                          <Clock className="h-3 w-3 mr-1" />
                          Rejected {(objective as RejectedObjective).rejected_at ? new Date((objective as RejectedObjective).rejected_at!).toLocaleDateString() : (objective as any).updated_at ? new Date((objective as any).updated_at).toLocaleDateString() : 'Recently'}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      {expandedObjective === objective.id ? (
                        <ChevronUp className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                  </div>
                </CardHeader>

                <AnimatePresence>
                  {expandedObjective === objective.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ObjectiveCard
                        objective={objective}
                        allowEdit={true}
                        isEditing={editingObjective === objective.id}
                        currentObj={getCurrentObjective(objective)}
                        savingEdit={savingEdit}
                        editedObjectives={editedObjectives}
                        canWithdrawObjectives={canWithdrawObjectives}
                        withdrawingObjective={withdrawingObjective}
                        userRole={userRole}
                        objectivesLookup={objectivesLookup}
                        initiativesLookup={initiativesLookup}
                        operationalLookup={operationalLookup}
                        onStartEdit={onStartEdit}
                        onCancelEdit={onCancelEdit}
                        onSaveEdit={onSaveEdit}
                        onUpdateField={onUpdateField}
                        onUpdateKPIField={onUpdateKPIField}
                        onAddKPI={onAddKPI}
                        onRemoveKPI={onRemoveKPI}
                        onSetEditedObjectives={onSetEditedObjectives}
                        onShowWithdrawDialog={onShowWithdrawDialog}
                        onShowDeleteDialog={onShowDeleteDialog}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </>
    )
  }

  // Pending tab - has unique inline approve/reject buttons and change history
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{subheading}</p>
      </div>
      <AnimatePresence mode="popLayout">
        {objectives.map((objective) => (
          <motion.div
            key={objective.id}
            layout
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, x: -100 }}
          >
            <Card className="overflow-hidden border-l-4 border-l-amber-500">
              <CardHeader
                className="pb-3 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedObjective(
                  expandedObjective === objective.id ? null : objective.id
                )}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <CardTitle className="text-lg">{objective.title}</CardTitle>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {objective.kpa && (
                        <Badge className={getFunctionalAreaColor(objective.kpa.name)}>
                          <BookOpen className="h-3 w-3 mr-1" />
                          {objective.kpa.display_name}
                        </Badge>
                      )}
                      {objective.pillar && !objective.strategic_objective_id && (
                        <Badge className={getPillarColor(objective.pillar.name)}>
                          {objective.pillar.name}
                        </Badge>
                      )}
                      {objective.strategic_objective_id && objectivesLookup.find(o => o.id === objective.strategic_objective_id) && (() => {
                        const strategicObj = objectivesLookup.find(o => o.id === objective.strategic_objective_id)!
                        return (
                          <Badge
                            style={{ backgroundColor: strategicObj.color_code || '#6B7280' }}
                            className="text-white"
                          >
                            {strategicObj.code}. {strategicObj.display_name}
                          </Badge>
                        )
                      })()}
                      {objective.strategic_initiative_id && initiativesLookup.find(i => i.id === objective.strategic_initiative_id) && (() => {
                        const initiative = initiativesLookup.find(i => i.id === objective.strategic_initiative_id)!
                        return (
                          <span title={`L2 Strategic Initiative: ${initiative.name}`}>
                            <Badge variant="outline" className="text-xs">
                              {initiative.code}
                            </Badge>
                          </span>
                        )
                      })()}
                      <Badge variant="outline" className="gap-1">
                        <Clock className="h-3 w-3" />
                        Submitted {objective.created_at ? new Date(objective.created_at).toLocaleDateString() : 'Recently'}
                      </Badge>
                      {editedObjectives[objective.id] && (
                        <Badge className="gap-1 bg-amber-100 text-amber-800 border-amber-300">
                          <Edit3 className="h-3 w-3" />
                          Edited
                        </Badge>
                      )}
                    </div>

                    {/* Inline Quick Actions */}
                    <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                      {userRole === 'manager' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-700 border-green-300 hover:bg-green-50"
                            onClick={() => onApprove(objective.id)}
                            disabled={processingObjective === objective.id || !canApproveObjectives}
                          >
                            {processingObjective === objective.id ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                            )}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-700 border-red-300 hover:bg-red-50"
                            onClick={() => {
                              if (!canApproveObjectives) {
                                const status = collaborativeSession?.status
                                if (!status) {
                                  toast.error(
                                    'Cannot reject objectives - no collaborative session exists. Please schedule and complete a meeting first.',
                                    { duration: 5000 }
                                  )
                                } else if (status === 'pending_scheduling' || status === 'pending-scheduling') {
                                  toast.error(
                                    'Cannot reject objectives - the collaborative session has not been scheduled yet. Please schedule and complete a meeting first.',
                                    { duration: 5000 }
                                  )
                                } else if (status === 'scheduled') {
                                  toast.error(
                                    'Cannot reject objectives until the collaborative session is completed. Please complete the scheduled meeting first.',
                                    { duration: 5000 }
                                  )
                                } else if (status === 'in-progress' || status === 'in_progress') {
                                  toast.error(
                                    'Cannot reject objectives - the collaborative session is in progress. Please conclude and mark the meeting as complete first.',
                                    { duration: 5000 }
                                  )
                                } else {
                                  toast.error(
                                    'Cannot reject objectives until the collaborative session is completed.',
                                    { duration: 5000 }
                                  )
                                }
                                return
                              }
                              onShowRejectDialog(objective.id)
                            }}
                            disabled={!canApproveObjectives}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {canWithdrawObjectives && !objective.was_previously_active && (
                        (userRole === 'manager' && objective.role_type === 'manager-initiated') ||
                        (userRole === 'employee' && objective.role_type === 'employee-initiated')
                      ) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-orange-700 border-orange-300 hover:bg-orange-50"
                          onClick={() => onShowWithdrawDialog(objective.id)}
                          disabled={withdrawingObjective === objective.id}
                        >
                          <Undo2 className="h-4 w-4 mr-1" />
                          Withdraw
                        </Button>
                      )}
                      {expandedObjective !== objective.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setExpandedObjective(objective.id)}
                        >
                          View Details
                          <ChevronDown className="h-4 w-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {expandedObjective === objective.id && (
                    <div className="flex-shrink-0">
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    </div>
                  )}
                </div>
              </CardHeader>

              <AnimatePresence>
                {expandedObjective === objective.id && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.15, ease: 'easeInOut' }}
                  >
                    <CardContent className="space-y-4 border-t pt-4">
                      {/* Edit Mode Toggle */}
                      <div className="flex justify-end">
                        {editingObjective === objective.id ? (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onCancelEdit(objective.id)}
                              disabled={savingEdit === objective.id}
                            >
                              <XIcon className="h-4 w-4 mr-1" />
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => onSaveEdit(objective.id)}
                              disabled={savingEdit === objective.id}
                            >
                              {savingEdit === objective.id ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Save className="h-4 w-4 mr-1" />
                                  Save Changes
                                </>
                              )}
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onStartEdit(objective)}
                          >
                            <Edit3 className="h-4 w-4 mr-1" />
                            Edit Objective
                          </Button>
                        )}
                      </div>

                      {/* Objective Details */}
                      {(() => {
                        const currentObj = getCurrentObjective(objective)
                        const isEditing = editingObjective === objective.id

                        return (
                          <>
                            <div className="space-y-4">
                              <div>
                                <Label className="text-sm font-semibold">Title</Label>
                                {isEditing ? (
                                  <Input
                                    value={currentObj.title}
                                    onChange={(e) => onUpdateField(objective.id, 'title', e.target.value)}
                                    className="mt-1"
                                  />
                                ) : (
                                  <p className="text-sm mt-1">{currentObj.title}</p>
                                )}
                              </div>

                              <div>
                                <Label className="text-sm font-semibold">Description</Label>
                                {isEditing ? (
                                  <Textarea
                                    value={currentObj.description}
                                    onChange={(e) => onUpdateField(objective.id, 'description', e.target.value)}
                                    className="mt-1 min-h-[80px]"
                                  />
                                ) : (
                                  <p className="text-sm text-muted-foreground mt-1 break-all">{currentObj.description}</p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-sm font-semibold">Start Date</Label>
                                {isEditing ? (
                                  <Input
                                    type="date"
                                    value={currentObj.start_date?.split('T')[0] || ''}
                                    onChange={(e) => onUpdateField(objective.id, 'start_date', e.target.value)}
                                    className="mt-1"
                                  />
                                ) : (
                                  <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                                    <Clock className="h-4 w-4" />
                                    {currentObj.start_date ? new Date(currentObj.start_date).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Not set'}
                                  </div>
                                )}
                              </div>
                              <div>
                                <Label className="text-sm font-semibold">End Date</Label>
                                {isEditing ? (
                                  <Input
                                    type="date"
                                    value={currentObj.end_date?.split('T')[0] || ''}
                                    onChange={(e) => onUpdateField(objective.id, 'end_date', e.target.value)}
                                    className="mt-1"
                                  />
                                ) : (
                                  <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                                    <Clock className="h-4 w-4" />
                                    {currentObj.end_date ? new Date(currentObj.end_date).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Not set'}
                                  </div>
                                )}
                              </div>
                            </div>

                            <Separator />
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm font-semibold flex items-center gap-2">
                                  <TrendingUp className="h-4 w-4" />
                                  Key Performance Indicators ({currentObj.kpis.length})
                                </Label>
                                {isEditing && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onAddKPI(objective.id)}
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add KPI
                                  </Button>
                                )}
                              </div>

                              {currentObj.kpis.length === 0 ? (
                                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                                  <Target className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                  <p className="text-sm text-muted-foreground">No KPIs defined yet</p>
                                  {isEditing && (
                                    <Button size="sm" variant="link" onClick={() => onAddKPI(objective.id)} className="mt-2">
                                      Add your first KPI
                                    </Button>
                                  )}
                                </div>
                              ) : isEditing ? (
                                /* Accordion KPIs for edit mode */
                                <div className="space-y-2">
                                  {currentObj.kpis.map((kpi, idx) => {
                                    const isKpiExpanded = expandedKpiId === kpi.id
                                    return (
                                      <div key={kpi.id} className="border rounded-lg overflow-hidden">
                                        <div className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 select-none" onClick={() => setExpandedKpiId(isKpiExpanded ? null : kpi.id)}>
                                          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform shrink-0 ${isKpiExpanded ? '' : '-rotate-90'}`} />
                                          <span className="text-sm font-medium flex-1 truncate">{kpi.name || `KPI ${idx + 1}`}</span>
                                          <span className="text-xs text-muted-foreground shrink-0">{formatKpiMeasurement(kpi)}</span>
                                          <span className="text-xs text-muted-foreground shrink-0 capitalize">{kpi.frequency}</span>
                                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0 shrink-0" onClick={(e) => { e.stopPropagation(); onRemoveKPI(objective.id, kpi.id) }}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                          </Button>
                                        </div>
                                        {isKpiExpanded && (
                                          <div className="px-4 pb-4 pt-2 border-t bg-slate-50/50 space-y-3">
                                            <div>
                                              <Label className="text-xs">KPI Name</Label>
                                              <Input value={kpi.name} onChange={(e) => onUpdateKPIField(objective.id, kpi.id, 'name', e.target.value)} placeholder="e.g., Student Pass Rate" className="mt-1" />
                                            </div>
                                            <div>
                                              <Label className="text-xs">Description</Label>
                                              <Textarea value={kpi.description || ''} onChange={(e) => onUpdateKPIField(objective.id, kpi.id, 'description', e.target.value)} placeholder="Brief description" className="mt-1 min-h-[60px]" />
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                              <div>
                                                <Label className="text-xs">Measurement Type</Label>
                                                <Select value={kpi.measurement_mode || 'numeric'} onValueChange={(value) => { onUpdateKPIField(objective.id, kpi.id, 'measurement_mode', value); if (value === 'percentage') onUpdateKPIField(objective.id, kpi.id, 'unit', '%'); else if (value === 'rating') { onUpdateKPIField(objective.id, kpi.id, 'target_value', 100); onUpdateKPIField(objective.id, kpi.id, 'unit', 'status') } else if (value === 'binary') { onUpdateKPIField(objective.id, kpi.id, 'target_value', 100); onUpdateKPIField(objective.id, kpi.id, 'unit', 'achieved') } }}>
                                                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                                  <SelectContent>
                                                    <SelectItem value="numeric">Numeric (count, days)</SelectItem>
                                                    <SelectItem value="percentage">Percentage (0-100%)</SelectItem>
                                                    <SelectItem value="rating">Rating Scale (RAG)</SelectItem>
                                                    <SelectItem value="binary">Binary (Achieved/Not)</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </div>
                                              <div>
                                                <Label className="text-xs">Frequency</Label>
                                                <Select value={kpi.frequency} onValueChange={(value) => onUpdateKPIField(objective.id, kpi.id, 'frequency', value)}>
                                                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                                  <SelectContent>
                                                    <SelectItem value="bi-annually">Bi-annually</SelectItem>
                                                    <SelectItem value="annually">Annually</SelectItem>
                                                    <SelectItem value="quarterly">Quarterly</SelectItem>
                                                    <SelectItem value="semester">Semester</SelectItem>
                                                    <SelectItem value="monthly">Monthly</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </div>
                                            </div>
                                            {(kpi.measurement_mode === 'rating' || kpi.measurement_mode === 'binary') ? (
                                              <div className={`p-2 rounded text-xs ${kpi.measurement_mode === 'rating' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                                                {kpi.measurement_mode === 'rating' ? 'Rating: Red (0%) / Amber (50%) / Green (100%)' : 'Binary: Achieved (100%) or Not Achieved (0%)'}
                                              </div>
                                            ) : (
                                              <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                  <Label className="text-xs">Target</Label>
                                                  <Input type="number" min={(kpi.measurement_mode || 'numeric') === 'percentage' ? 0 : undefined} max={(kpi.measurement_mode || 'numeric') === 'percentage' ? 100 : undefined} value={kpi.target_value} onChange={(e) => onUpdateKPIField(objective.id, kpi.id, 'target_value', parseFloat(e.target.value))} className="mt-1" />
                                                </div>
                                                <div>
                                                  <Label className="text-xs">Unit</Label>
                                                  {(kpi.measurement_mode || 'numeric') === 'percentage' ? <Input value="%" disabled className="mt-1 bg-gray-100" /> : <Input value={kpi.unit} onChange={(e) => onUpdateKPIField(objective.id, kpi.id, 'unit', e.target.value)} placeholder="days, count" className="mt-1" />}
                                                </div>
                                              </div>
                                            )}
                                            {(kpi.measurement_mode || 'numeric') !== 'rating' && (kpi.measurement_mode || 'numeric') !== 'binary' && (
                                              <div>
                                                <Label className="text-xs">Progress Direction</Label>
                                                <Select value={kpi.measurement_direction || 'higher_is_better'} onValueChange={(value) => onUpdateKPIField(objective.id, kpi.id, 'measurement_direction', value)}>
                                                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                                  <SelectContent>
                                                    <SelectItem value="higher_is_better">↑ Higher is Better</SelectItem>
                                                    <SelectItem value="lower_is_better">↓ Lower is Better</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                              ) : (
                                /* Compact table for view mode */
                                <div className="border rounded-lg overflow-hidden">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="bg-slate-50 text-left">
                                        <th className="px-3 py-2 font-medium text-xs text-slate-500">KPI</th>
                                        <th className="px-3 py-2 font-medium text-xs text-slate-500 text-right w-20">Target</th>
                                        <th className="px-3 py-2 font-medium text-xs text-slate-500 w-16">Unit</th>
                                        <th className="px-3 py-2 font-medium text-xs text-slate-500 w-24">Frequency</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                      {currentObj.kpis.map((kpi) => (
                                        <tr key={kpi.id} className="hover:bg-slate-50/50">
                                          <td className="px-3 py-2">
                                            <div className="flex items-center gap-1">
                                              <span className="font-medium truncate">{kpi.name}</span>
                                              {kpiDirectionIcon(kpi)}
                                              {kpi.description && (
                                                <span title={kpi.description} className="shrink-0 cursor-help"><Info className="h-3.5 w-3.5 text-slate-400" /></span>
                                              )}
                                            </div>
                                          </td>
                                          <td className="px-3 py-2 text-right font-semibold tabular-nums">
                                            {kpi.measurement_mode === 'rating' ? 'RAG' : kpi.measurement_mode === 'binary' ? '—' : kpi.target_value}
                                          </td>
                                          <td className="px-3 py-2 text-muted-foreground">{kpi.unit}</td>
                                          <td className="px-3 py-2 text-muted-foreground capitalize">{kpi.frequency}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>

                            <Separator />
                            {isEditing ? (
                              <StrategicContextSelector
                                value={{
                                  strategic_objective_id: currentObj.strategic_objective_id,
                                  strategic_initiative_id: currentObj.strategic_initiative_id,
                                  operational_initiative_id: currentObj.operational_initiative_id
                                }}
                                onChange={(context: StrategicContext) => {
                                  onSetEditedObjectives(prev => ({
                                    ...prev,
                                    [objective.id]: {
                                      ...prev[objective.id],
                                      strategic_objective_id: context.strategic_objective_id,
                                      strategic_initiative_id: context.strategic_initiative_id,
                                      operational_initiative_id: context.operational_initiative_id
                                    }
                                  }))
                                }}
                                compact
                              />
                            ) : (
                              <div>
                                <Label className="text-sm font-semibold flex items-center gap-2">
                                  <Goal className="h-4 w-4" />
                                  Strategic Context (Optional)
                                </Label>
                                {(currentObj.strategic_objective_id || currentObj.strategic_initiative_id || currentObj.operational_initiative_id) ? (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {currentObj.strategic_objective_id && objectivesLookup.find(o => o.id === currentObj.strategic_objective_id) && (() => {
                                      const strategicObj = objectivesLookup.find(o => o.id === currentObj.strategic_objective_id)!
                                      return (
                                        <span title={`L1 Strategic Objective: ${strategicObj.display_name}`}>
                                          <Badge
                                            style={{ backgroundColor: strategicObj.color_code || '#6B7280' }}
                                            className="text-white"
                                          >
                                            L1: {strategicObj.code}. {strategicObj.display_name}
                                          </Badge>
                                        </span>
                                      )
                                    })()}
                                    {currentObj.strategic_initiative_id && initiativesLookup.find(i => i.id === currentObj.strategic_initiative_id) && (() => {
                                      const initiative = initiativesLookup.find(i => i.id === currentObj.strategic_initiative_id)!
                                      return (
                                        <span title={`L2 Strategic Initiative: ${initiative.name}`}>
                                          <Badge variant="outline">
                                            L2: {initiative.code} {initiative.name}
                                          </Badge>
                                        </span>
                                      )
                                    })()}
                                    {currentObj.operational_initiative_id && operationalLookup.find(o => o.id === currentObj.operational_initiative_id) && (() => {
                                      const operational = operationalLookup.find(o => o.id === currentObj.operational_initiative_id)!
                                      return (
                                        <span title={`L3 Operational Initiative: ${operational.name}`}>
                                          <Badge variant="outline" className="text-xs">
                                            L3: {operational.code}
                                          </Badge>
                                        </span>
                                      )
                                    })()}
                                  </div>
                                ) : (
                                  <p className="text-sm text-muted-foreground mt-2">No strategic context linked</p>
                                )}
                              </div>
                            )}

                            {currentObj.strategic_objective && !currentObj.strategic_objective_id && (
                              <div className="mt-3">
                                <Label className="text-sm font-semibold">Aligned with Strategic Objective</Label>
                                <div className="flex items-center gap-2 mt-1">
                                  <Goal className="h-4 w-4 text-heritage-blue" />
                                  <span className="text-sm">{currentObj.strategic_objective.title}</span>
                                </div>
                              </div>
                            )}
                          </>
                        )
                      })()}

                      {/* Recent Changes Section */}
                      {objective.status === 'pending_approval' && objectiveChanges[objective.id] && objectiveChanges[objective.id].length > 0 && (
                        <>
                          <Separator />
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <History className="h-4 w-4 text-blue-600" />
                              <Label className="text-sm font-semibold text-blue-900">Recent Changes</Label>
                              <Badge variant="outline" className="text-xs">
                                {objectiveChanges[objective.id].length} {objectiveChanges[objective.id].length === 1 ? 'change' : 'changes'}
                              </Badge>
                            </div>

                            <div className="space-y-2 max-h-60 overflow-y-auto">
                              {objectiveChanges[objective.id].slice(0, 5).map((change: any, idx: number) => (
                                <Card key={change.id} className="bg-blue-50 border-blue-200">
                                  <CardContent className="p-3 space-y-2">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <Badge className={
                                            change.changed_by_role === 'employee'
                                              ? 'bg-blue-100 text-blue-800 text-xs'
                                              : 'bg-purple-100 text-purple-800 text-xs'
                                          }>
                                            {change.changed_by_role === 'employee' ? 'Team Member' : 'People Leader'}
                                          </Badge>
                                          <span className="text-xs text-gray-600">
                                            {new Date(change.created_at).toLocaleString('en-ZA', {
                                              day: '2-digit',
                                              month: 'short',
                                              year: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            })}
                                          </span>
                                        </div>

                                        {change.change_summary && (
                                          <p className="text-sm text-gray-700 mb-2">{change.change_summary}</p>
                                        )}

                                        {change.changes && Object.keys(change.changes).length > 0 && (
                                          <div className="space-y-1 text-xs">
                                            {Object.entries(change.changes).slice(0, 3).map(([field, values]: [string, any]) => (
                                              <div key={field} className="flex items-start gap-2">
                                                <ArrowRight className="h-3 w-3 text-blue-600 flex-shrink-0 mt-0.5" />
                                                <div className="flex-1">
                                                  <span className="font-medium capitalize">{field.replace(/_/g, ' ')}:</span>
                                                  {values.old && (
                                                    <span className="text-gray-500 line-through ml-1">{String(values.old).substring(0, 50)}</span>
                                                  )}
                                                  {values.new && (
                                                    <span className="text-blue-700 font-medium ml-1">{String(values.new).substring(0, 50)}</span>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>

                            {objectiveChanges[objective.id].length > 5 && (
                              <p className="text-xs text-gray-500 text-center">
                                Showing most recent 5 of {objectiveChanges[objective.id].length} changes
                              </p>
                            )}
                          </div>
                        </>
                      )}

                      {/* Loading Changes Indicator */}
                      {objective.status === 'pending_approval' && loadingChanges[objective.id] && (
                        <>
                          <Separator />
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading change history...</span>
                          </div>
                        </>
                      )}

                      {/* Manager Feedback and Actions */}
                      {userRole === 'manager' && (
                        <>
                          <Separator />

                          <div className="space-y-3">
                            <Label className="text-sm font-semibold">People Leader Feedback (Optional)</Label>
                            <Textarea
                              placeholder="Provide constructive feedback or guidance..."
                              value={approvalFeedback[objective.id] || ''}
                              onChange={(e) => setApprovalFeedback(prev => ({
                                ...prev,
                                [objective.id]: e.target.value
                              }))}
                              className="min-h-[80px]"
                              disabled={editingObjective === objective.id}
                            />
                          </div>

                          {userRole === 'manager' && !canApproveObjectives && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <p className="text-sm text-amber-800 flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 shrink-0" />
                                {collaborativeSession?.held_with_year_end_checkin && year
                                  ? yearEndApprovalNote(collaborativeSession, year)
                                  : 'Complete the collaborative session above before approving objectives.'}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          </motion.div>
        ))}
      </AnimatePresence>
    </>
  )
}
